// Building glyphs for the construct-building flow: the picker buttons (React
// SVG) and the placement cursor (a data-URI SVG cursor) draw from one set of
// path definitions, so the icon the player picks is the icon their cursor
// becomes. Bridge isn't here — it has its own action, not the building picker.

import type { ConstructableBuilding, ImplementPiece } from "@fortress/shared";

/** The buildings the construct-building picker offers (Scout / Barracks / Mine). */
export const CONSTRUCTABLE_BUILDINGS: readonly ConstructableBuilding[] = [
  "scout",
  "barracks",
  "mine",
];

export const BUILDING_LABELS: Record<ConstructableBuilding, string> = {
  scout: "Scout",
  barracks: "Barracks",
  mine: "Mine",
};

/** Labels for every piece an implement-time pick may place on a card
 * (Shifty Engineer's scout / bridge). */
export const PIECE_LABELS: Record<ImplementPiece, string> = {
  ...BUILDING_LABELS,
  bridge: "Bridge",
};

/** One-line description per building, for the picker buttons' tooltips. */
export const BUILDING_BLURBS: Record<ConstructableBuilding, string> = {
  scout: "Cheap, long build reach (range 7).",
  barracks: "Attacks & defends; blocks opponents building through it.",
  mine: "Yields its node's resource each Production (range 2).",
};

/**
 * The bare glyph path per building, in a 24×24 box, stroked (no fill):
 *  - scout  → a flag on a pole,
 *  - barracks → a house / A-frame with a door,
 *  - mine   → a pickaxe (handle + arced head).
 * Shared by the React icon and the cursor so both always match.
 */
const GLYPH_PATHS: Record<ConstructableBuilding, string> = {
  scout: "M7 21 V3 L17 6.5 L7 10",
  barracks: "M4 20 L12 5 L20 20 M8 20 V13 H16 V20",
  mine: "M11 21 V10 M4 10 Q11 4 18 10",
};

/** A bridge glyph — two posts under an arched deck — for the implement-choice
 * picker, which offers "bridge" beside the buildings. */
const BRIDGE_GLYPH_PATH = "M3 16 H21 M7 16 V10 M17 16 V10 M5 11 Q12 5 19 11";

/** Inline icon for any piece an implement-time pick may denote. */
export function PieceIcon({
  kind,
  size = 20,
  color = "currentColor",
}: {
  kind: ImplementPiece;
  size?: number;
  color?: string;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={kind === "bridge" ? BRIDGE_GLYPH_PATH : GLYPH_PATHS[kind]}
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Inline building icon for the picker buttons (inherits `color` via stroke). */
export function BuildingIcon({
  kind,
  size = 20,
  color = "currentColor",
}: {
  kind: ConstructableBuilding;
  size?: number;
  color?: string;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={GLYPH_PATHS[kind]}
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A CSS `cursor` value showing the building glyph — dark ink with a white halo
 * so it reads on the light parchment board. Hotspot is the glyph centre, with a
 * `crosshair` fallback. Used while placing the selected building.
 */
export function buildingCursor(kind: ConstructableBuilding): string {
  const d = GLYPH_PATHS[kind];
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none">` +
    `<path d="${d}" stroke="#fff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>` +
    `<path d="${d}" stroke="#2b2620" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 15 15, crosshair`;
}
